import React, { FunctionComponent, useEffect, useState } from 'react'
import { Box, Checkbox, Chip, FormControl, FormHelperText, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField, Typography } from '@mui/material'
import { IInviteUserDetails, IRole } from '../../../../shared/services/users-teams/users-teams.model'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import UserDetailsService from '../../../../shared/services/user-details/user-details.service';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper';
import { notifyError } from '../../../../shared/utils/notify-error';
import { useDirtyBaseline } from '../../../../hooks/use-dirty-baseline';

export const InviteUserDialog: FunctionComponent<{
  dialogDetails: IInviteUserDetails,
  open: boolean,
  closeHandler: () => void,
  getUsers: () => void,
  roles?: IRole[],
  selectedRoles: string[],
  setSelectedRoles: React.Dispatch<React.SetStateAction<any>>,
  isEdit: boolean,
}> = ({
  dialogDetails,
  open,
  closeHandler,
  roles,
  getUsers,
  selectedRoles,
  setSelectedRoles,
  isEdit,
}) => {
    //Services
    const userDetailsService = new UserDetailsService();
    const subsidiaryService = SubsidiaryService.getInstance();
    const userNotificationsContext = React.useContext(UserNotificationsContext);
    const screenLabelsContext = React.useContext(ScreenLabelsContext);
    //State
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')
    const [errorCode, setErrorCode] = useState<number>()
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    //The default subsidiary drives the user's preselected defaults (spend factor source, import
    //wizard); editable here by an admin as well as by the user on their own profile page. Invites
    //assign the organisation default automatically, so the field only renders in edit mode.
    useEffect(() => {
      if (!isEdit) return
      subsidiaryService.getAll()
        .then(response => setSubsidiaries(response))
        .catch(err => console.log(err))
    }, [isEdit])
    //Formik
    const inviteUserSchema = Yup.object().shape({
      firstName: Yup.string().required('First name is required'),
      surname: Yup.string().required('Last name is required'),
      email: Yup.string()
        .email('Email is invalid')
        .test('is-company-email', 'Please use a company email address', (value) => {
          if (!value) return true
          const lowerValue = value.toLowerCase()
          // Block all @gmail.com emails except sjnaunton+*@gmail.com
          if (lowerValue.endsWith('@gmail.com')) {
            const isAllowed = lowerValue.startsWith('sjnaunton+')
            return isAllowed
          }
          return true
        })
        .required('Email is required'),
      roles: Yup.array().of(Yup.string().required('Role is required')).min(1, 'At least one role is required'),
      ...(isEdit ? { defaultSubsidiaryId: Yup.string().required(`Default ${screenLabelsContext.getLabel('subsidiary', { plural: false })} is required`) } : {}),
    })
    const formik = useFormik({
      initialValues: {
        firstName: dialogDetails.firstName,
        surname: dialogDetails.surname,
        email: dialogDetails.email,
        roles: selectedRoles,
        defaultSubsidiaryId: dialogDetails.defaultSubsidiaryId || ''
      },
      validationSchema: inviteUserSchema,
      onSubmit: (values) => {
        if (!isSubmitting) {
          if (isEdit === false) {
            setIsSubmitting(true)
            userDetailsService.inviteUserToOrganisation(values)
              .then(() => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Invitation Sent' });
                closeHandler();
                getUsers();
                formik.resetForm();
                setErrorMessage('')
                setSelectedRoles([])
                setIsSubmitting(false)
              })
              .catch(err => {
                if (err.response?.data?.message) {
                  setErrorCode(err.response.data.code)
                  setErrorMessage(err.response.data.message)
                }
                notifyError(userNotificationsContext.openSnackbar, err, 'sending the invitation')
                setIsSubmitting(false)
              })
          }
          if (isEdit === true && dialogDetails.id) {
            setIsSubmitting(true)
            userDetailsService.editAnotherUserDetails(dialogDetails.id, values)
              .then(() => {
                userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
                closeHandler();
                getUsers();
                formik.resetForm();
                setErrorMessage('')
                setSelectedRoles([])
                setIsSubmitting(false)
              })
              .catch(err => {
                if (err.response?.data?.message) {
                  setErrorCode(err.response.data.code)
                  setErrorMessage(err.response.data.message)
                }
                notifyError(userNotificationsContext.openSnackbar, err, 'saving the user')
                setIsSubmitting(false)
              })
          }
        }
      }
    });
    // Dirty tracking. formik.dirty is unusable here: the effect below seeds the form via
    // setValues whenever the edited user changes, which would mark it dirty on open.
    const { isDirty, rebaseline } = useDirtyBaseline(formik.values)

    //Use Effect
    useEffect(() => {
      formik.setValues({
        firstName: dialogDetails.firstName,
        surname: dialogDetails.surname,
        email: dialogDetails.email,
        roles: dialogDetails.roles,
        defaultSubsidiaryId: dialogDetails.defaultSubsidiaryId || '',
      })
      setSelectedRoles(dialogDetails.roles)
      // Full snapshot is safe here: this seed rewrites every field the form has, so the
      // baseline always matches exactly what the form now shows.
      rebaseline()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogDetails])


    const selectRoleHandler = (e: SelectChangeEvent<string[]>) => {
      const { value } = e.target;
      setSelectedRoles(typeof value === 'string' ? value.split(',') : value,);
      formik.setFieldValue('roles', typeof value === 'string' ? value.split(',') : value,)
    }

    const cancelHandler = () => {
      closeHandler();
      formik.resetForm();
      setErrorMessage('')
      setSelectedRoles([])
    }

    return (
      <DialogWrapper
        open={open}
        onClose={cancelHandler}
        title={isEdit ? 'Edit User' : 'Invite User'}
        cancelHandler={cancelHandler}
        submitHandler={formik.submitForm}
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        submitButtonText={isEdit ? 'Save' : 'Send Invitation'}
        submitButtonLoadingText={isEdit ? 'Saving' : 'Sending Invitation'}
      >
        <Box component='form' onSubmit={formik.handleSubmit}>
            <Typography variant='body1'>
              {!isEdit && 'To invite a user, please enter their details below'}
            </Typography>
            <TextField id='firstName' name='firstName' label='First Name' type='firstName' fullWidth
              value={formik.values.firstName}
              onChange={formik.handleChange} sx={{ mt: 2 }}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
            />
            {errorCode === 2 &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
              </FormHelperText>
            }
            <TextField
              id='surname' name='surname' label='Last Name' type='surname' fullWidth value={formik.values.surname} onChange={formik.handleChange} sx={{ mt: 2 }}
              error={formik.touched.surname && Boolean(formik.errors.surname)}
              helperText={formik.touched.surname && formik.errors.surname}
            />
            {errorCode === 3 &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
              </FormHelperText>
            }
            <TextField id='email' name='email' label='Email' type='email' fullWidth value={formik.values.email} onChange={formik.handleChange} sx={{ mt: 2 }}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
            {errorCode === 1 &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
              </FormHelperText>
            }
            {roles && roles.length > 0 && <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel htmlFor='roles'>Role</InputLabel>
              <Select id='roles' label='roles' name='roles'
                multiple
                value={selectedRoles}
                error={formik.touched.roles && Boolean(formik.errors.roles)}
                onChange={selectRoleHandler}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => {
                      const selectedName = roles.find(category => category.code === value)?.name
                      return (
                        <Chip key={index} label={selectedName} />
                      )
                    })}
                  </Box>
                )}
                input={<OutlinedInput label="Roles" />}
              >
                {/* // Just for now given now have partner v client */}
                {roles.filter(role => role.code !== 'Client').map((role) => (
                  <MenuItem key={role.id} value={role.code}>
                    <Checkbox checked={selectedRoles.includes(role.code)} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.roles && formik.errors.roles &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.roles}</FormHelperText>
              }
              {errorCode === 4 &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
                </FormHelperText>
              }
            </FormControl>}
            {isEdit && subsidiaries && subsidiaries.length > 0 && <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel htmlFor='defaultSubsidiaryId'>{`Default ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`}</InputLabel>
              <Select
                id='defaultSubsidiaryId' name='defaultSubsidiaryId'
                label={`Default ${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}`}
                value={formik.values.defaultSubsidiaryId}
                onChange={formik.handleChange}
                error={formik.touched.defaultSubsidiaryId && Boolean(formik.errors.defaultSubsidiaryId)}
              >
                {getSelectableSubsidiaries(subsidiaries, formik.values.defaultSubsidiaryId).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary) => (
                  <MenuItem key={subsidiary.id} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Sets which {screenLabelsContext.getLabel('subsidiary', { plural: false })} this user's defaults follow, including the default spend factor source for new entries and imports
              </FormHelperText>
              {formik.touched.defaultSubsidiaryId && formik.errors.defaultSubsidiaryId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.defaultSubsidiaryId as string}</FormHelperText>
              }
              {errorCode === 6 &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
              }
            </FormControl>}
            {errorCode === 5 &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
              </FormHelperText>
            }
        </Box>
      </DialogWrapper>
    )
  }
